import { useState, useEffect } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import Button from "../../../../common/Button";
import CustomInput from "../../../../common/CustomInput";
import {
  createMedicalCondition,
  updateMedicalCondition,
} from "../../../../../redux/modules/clinical-services/actions";
import { acuteMCEnum, chronicMCEnum, getMCEnumHeader } from "./data";
import { useDispatch } from "react-redux";
import Alert from "../../../../common/Alert";
import CommonSlider from "../../../../common/CommonSlider";
import MedicalConditionSelect from "../../services/select/medical-condition";

export default function MedicalConditionCreate({
  open,
  onClose,
  onSkip,
  onSave,
  step = null,
  conditionOption,
  selectedCondition,
  patientId,
  create = true,
  updateSymptomControl,
  patientMedicalConditions = [],
  isAcuteCreation,
  fromClinicalService = false
}) {
  const sourceOptions = [
    { value: "patient", label: "Patient" },
    { value: "caregiver", label: "Alternate Caregiver" },
    { value: "physician", label: "Physician" },
    { value: "other", label: "Other Healthcare Provider" },
  ];
  const MedicalConditionOptions = [
    ...acuteMCEnum.options
      .filter((s) => s.value !== "None")
      .map((i) => ({ value: i.value, label: i.header })),
    ...chronicMCEnum.options
      .filter((s) => s.value !== "None")
      .map((i) => ({ value: i.value, label: i.header })),
  ];
  const SymptomControlOptions = fromClinicalService
    ? [
        { value: "controlled", label: "Symptoms Controlled" },
        { value: "not controlled", label: "Symptoms Not Controlled" },
      ]
    : [
        { value: "controlled", label: "Symptoms Controlled" },
        { value: "not controlled", label: "Symptoms Not Controlled" },
        { value: "not sure", label: "Not Sure Symptoms Controlled" },
        { value: "not specified", label: "Not Specified" },
      ];

  //State Variables
  const dispatch = useDispatch();

  //Local Variables
  const [condition, setCondition] = useState(null); //vigilance
  const [symptomControl, setSymptomControl] = useState(create ? null : "notSpecified");
  const [source, setSource] = useState(
    create ? "patient" : selectedCondition.source
  );
  const [diagnosisDate, setDiagnosisDate] = useState(
    create ? null : selectedCondition.diagnosisDate
  );
  const [diagnosisDateOption, setDiagnosisDateOption] = useState(create ? null : selectedCondition.diagnosisDateOption);
  const [overriddenDate, setOverriddenDate] = useState(
    create ? null : selectedCondition.diagnosisDate
  );
  const [conditionNameInputMode, setConditionNameInputMode] = useState(
    create
      ? false
      : selectedCondition.medicalConditionGenCode === null
        ? true
        : false
  );
  const [comments, setComments] = useState(
    create ? "" : selectedCondition.comments
  );
  const [symptomControlError, setSymptomControlError] = useState(false);

  useEffect(() => {
    setSource(create ? "patient" : selectedCondition.source);
    setCondition({
      label: conditionOption?.value,
      name: conditionOption?.value,
      value: conditionOption?.code,
      id: conditionOption?.code,
      displayName: getMCEnumHeader(conditionOption?.value)
    });
  }, [open, conditionOption]);

  useEffect(() => {
    setCondition(
      create
        ? {
          label: conditionOption?.value,
          name: conditionOption?.value,
          value: conditionOption?.code,
          id: conditionOption?.code,
          displayName: getMCEnumHeader(conditionOption?.value)
        }
        : {
          label: selectedCondition?.conditionName,
          name: selectedCondition?.conditionName,
          value: selectedCondition?.medicalConditionGenCode,
          id: selectedCondition?.medicalConditionGenCode,
          displayName: getMCEnumHeader(selectedCondition?.conditionName)
        }
    );
    if (create) {
      setSymptomControl(null);
    } else {
      const rawValue = selectedCondition?.symptomControlled;
      if (fromClinicalService) {
        // For red flag section, always require user to fill Symptoms Controlled when updating
        setSymptomControl(null);
      } else {
        // Map null/empty symptomControlled to "not specified" for dropdown display
        setSymptomControl(rawValue == null || rawValue === "" ? "not specified" : rawValue);
      }
    }
    setSource(create ? "patient" : selectedCondition.source);
    setDiagnosisDate(create ? null : selectedCondition.diagnosisDate);
    setDiagnosisDateOption(create ? null : selectedCondition.diagnosisDateOption);
    setOverriddenDate(create ? null : selectedCondition.diagnosisDate);
    setComments(create ? "" : selectedCondition.comments);
  }, [selectedCondition]);

  const resetForm = () => {
    setCondition(null);
    setSymptomControlError(false);
    setSymptomControl(null);
    setSource("patient");
    setComments("");
    setDiagnosisDate(null);
    setDiagnosisDateOption(null);
    setOverriddenDate(null);
  };

  const isChronicType = () => {
    return chronicMCEnum.options.some(
      (i) => i.value === conditionOption?.value
    );
  }

  const isDuplicateCondition = () => {
    return !create &&
      (acuteMCEnum.options.some(
        (i) => i.value === conditionOption?.value
      ) ||
        chronicMCEnum.options.some(
          (i) => i.value === conditionOption?.value
        ))
  }

  const manageStep = (isCloseAndContinue = false) => {
    if (isCloseAndContinue) {
      onClose(false);
      resetForm();
      onSave();
    } else {
      let allFieldsValid =
        source !== null &&
        (chronicMCEnum.options.some((i) => i.value === conditionOption?.value)
          ? symptomControl !== null
          : true);

      if (allFieldsValid) {
        // Persist string value to backend (null maps to "not specified" for explicit option)
        const symptomControlledValue = symptomControl == null ? "not specified" : symptomControl;
        if (create) {
          let currentCondition = {
            medicalConditions: [
              {
                name: condition?.label,
                medicalConditionGenCode: conditionNameInputMode
                  ? null
                  : condition?.value,
                symptomControlled: symptomControlledValue,
                source: source,
                comments: comments,
                diagnosisDate: diagnosisDate || new Date(),
                diagnosisDateOption: diagnosisDateOption,
                overrideDate: new Date(),
                commentsToClinicalAlert: false,
                status: "active",
              },
            ],
          };
          dispatch(createMedicalCondition(patientId, currentCondition, fromClinicalService));
        } else {
          let currentCondition = {
            medicalConditions: [
              {
                id: selectedCondition.id,
                name: condition?.label,
                medicalConditionGenCode: conditionNameInputMode
                  ? null
                  : condition?.value,
                symptomControlled: symptomControlledValue,
                source: source,
                comments: comments,
                diagnosisDate: diagnosisDate || new Date(),
                diagnosisDateOption: diagnosisDateOption,
                overrideDate: new Date(),
                commentsToClinicalAlert: false,
                status: "active",
              },
            ],
          };
          dispatch(updateMedicalCondition(patientId, currentCondition, fromClinicalService));
        }
        updateSymptomControl(conditionOption?.value, symptomControlledValue);
        onClose(false);
        resetForm();
        onSave();
      }
    }
  };

  // const isValidConditionName = () => {
  //   return (acuteMCEnum.options.some((i) => i.value === conditionOption?.value) || chronicMCEnum.options.some((i) => i.value === conditionOption?.value));
  // }

  return (
    <CommonSlider open={open} onClose={onClose}>
      <div className="flex flex-col flex-1 h-full overflow-y-auto ">
        <div className="relative flex-1">
          {/* Header */}
          <div className="px-8 py-6 bg-gray-20">
            <div className="flex items-start justify-between space-x-3">
              <div className="flex space-x-1">
                <h2 className="font-bold text-gray-900 text-display-xs">
                  {create ? "Add" : "Assess"} {isAcuteCreation ? "Acute" : "Chronic"} Medical Condition
                </h2>
              </div>
              <div className="flex items-center h-7">
                <button
                  type="button"
                  className="flex items-center justify-center p-1 transition-all rounded-full max-w-8 cursor-pointer right-4 top-3 bg-blue-200 hover:bg-REELTeal hover:text-white text-teal-300"
                  onClick={() => onClose(false)}
                >
                  <span className="sr-only">Close panel</span>
                  <XMarkIcon className="w-5 h-5" aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>
          {isDuplicateCondition() && (
            <Alert
              type="warning"
              title={
                "DUPLICATE MEDICAL CONDITION - " +
                conditionOption?.value.toUpperCase()
              }
              content={() => isChronicType() ? "Complete SYMPTOM CONTROL to proceed with Assessment." : ""}
              isWrapped={true}
              hideCross={true}
            />
          )}
          {step != null && (
            <div className="text-sm flex justify-end px-8 pt-8">
              {step} of 2
            </div>
          )}

          <div className={"px-8 pb-6" + (step == null ? " pt-6" : " pt-2")}>
            <div className="grid grid-cols-12">
              <div className="col-span-12 !mb-[1.5rem]">
                {
                  <MedicalConditionSelect
                    isRequired={true}
                    disabled={isDuplicateCondition()}
                    onNewCreate={(value) => setConditionNameInputMode(value)}
                    value={condition}
                    onChange={(condition) => {
                      setCondition(condition);
                    }}
                    patientMedicalConditions={patientMedicalConditions}
                    error={condition && !condition?.value}
                  />
                }
                {conditionNameInputMode === true && (
                  <>
                    <p className="col-span-12 text-body-sm text-red-200 font-medium">
                      This is an UNCODED MEDICAL CONDITION- No Clinical Check
                      will be performed.
                    </p>
                  </>
                )}
              </div>
              {chronicMCEnum.options.some(
                (i) => i.value === conditionOption?.value
              ) && (
                  <>
                    <CustomInput
                      options={SymptomControlOptions}
                      name="symptomControl"
                      className="col-span-12 bg-yellow-50 !ring-yellow-600"
                      label="Symptom Control"
                      type="dropdown"
                      value={symptomControl}
                      onChange={(e) => setSymptomControl(e.value)}
                      required
                    />
                    {condition?.name === "asthma" ? (
                      <div className="col-span-12 text-xs text-end mb-[1rem]">
                        Unsure?&nbsp;
                        <a
                          href="https://ginasthma.org/wp-content/uploads/2025/11/GINA-Summary-Guide-2025-WEB_FINAL-WMS.pdf"
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="View GINA Asthma Guidelines"
                          className="!text-teal-300 text-sm underline"
                        >
                          <span>View Guidelines</span>
                        </a>
                      </div>
                    ) : (
                      <div className="col-span-12 text-xs text-end mb-[1.5rem]"></div>
                    )}
                  </>
                )}
              <CustomInput
                options={sourceOptions}
                name="source"
                className={`col-span-12 mb-[1.5rem]  `}
                label="Source"
                type="dropdown"
                value={source}
                onChange={(e) => setSource(e.value)}
                required
              />
              <CustomInput
                name="diagnosisDate"
                type="date"
                className="col-span-12 mb-[1.5rem]"
                label="Select Date"
                placeholder="Select Date"
                value={diagnosisDate}
                dateOptionValue={diagnosisDateOption}
                showDateOptions={true}
                onDateOptionChange={(dateVal, dateOptionVal) => {
                  setDiagnosisDate(dateVal);
                  setDiagnosisDateOption(dateOptionVal);
                }}
              />
              <CustomInput
                name="comments"
                className="col-span-12 mb-[1.5rem]"
                label="Comments"
                placeholder="Comments"
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                type="textarea"
                inputClassName="min-h-[48px] max-h-[72px] overflow-y-auto resize-none whitespace-pre-wrap break-words leading-6"
              />
              <div className="col-span-12 text-sm font-normal pointer-events-none mb-[1.5rem]">
                <input type="checkbox" />
                <label className="ml-2 opacity-40">Add Comments to Clinical Alerts - Coming Soon!</label>
              </div>
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center justify-center py-6">
          <div className="flex items-center justify-center space-x-5">
            <Button
              className="min-w-[200px]"
              text="Cancel"
              type="primary-outline"
              onClick={() => {
                onClose(false);
                resetForm();
              }}
            />
            {isAcuteCreation && patientMedicalConditions?.length > 0 && (
              <Button
                className="min-w-[200px]"
                text="Skip & Continue"
                type="primary-outline"
                onClick={() => {
                  resetForm();
                  onSkip();
                }}
              />
            )}
            <Button
              className="min-w-[200px]"
              disabled={isChronicType() ? symptomControl == null :false}
              text={`${step === 1 ? (isDuplicateCondition() ? "Update & Save" : "Save & Continue") : !(isDuplicateCondition()) ? "Save" : "Update & Save"}`}
              type="primary"
              onClick={() => {
                manageStep();
              }}
            />
          </div>
        </div>
      </div>
    </CommonSlider>
  );
}
